/**
 * Phase 5 PR-A — Sysadmin Governance hooks.
 *
 * TanStack Query hooks built on the `createSysadminQuery` +
 * `createSysadminMutation` factories so every consumer points at the
 * same cache entry + the same Zod-validated wire shape.
 *
 * Maps 1:1 onto `packages/api/src/routes/v1/admin/governance/*`:
 *
 *   Guardrails:
 *     useSysadminGuardrailProfiles()                — GET list (3 rows)
 *     usePatchSysadminGuardrailProfile()            — PATCH /:name
 *
 *   Compliance:
 *     useSysadminComplianceProfiles()               — GET list (5 frameworks)
 *     useSysadminComplianceProfile({ framework })   — GET /:framework
 *     usePatchSysadminComplianceProfile()           — PATCH /:framework
 *
 *   Classification:
 *     useSysadminClassificationRules({...})         — GET list (cursor)
 *     useCreateSysadminClassificationRule()         — POST
 *     usePatchSysadminClassificationRule()          — PATCH /:id
 *     useDeleteSysadminClassificationRule()         — DELETE /:id
 *     useSysadminSignalWords()                      — GET dictionary
 *     usePatchSysadminSignalWords()                 — PATCH dictionary
 */

import { z } from 'zod';
import {
  createSysadminQuery,
  createSysadminMutation,
  useSysadminQuery,
} from './use-sysadmin-query';

// ─── Shared Zod shapes (wire-aligned to the API) ────────────────────────────

const RegexPatternSchema = z.object({
  id: z.string(),
  pattern: z.string(),
  description: z.string().optional(),
});

export const GuardrailProfileSchema = z.object({
  name: z.enum(['basic', 'standard', 'maximum']),
  displayName: z.string(),
  latencyBudgetMs: z.number(),
  layers: z.array(z.string()),
  regexPatterns: z.array(RegexPatternSchema),
  injectionPatterns: z.array(RegexPatternSchema),
  topicDriftThreshold: z.number(),
  topicDriftEnabled: z.boolean(),
  topicDriftWindow: z.number().optional(),
  llmClassifierEnabled: z.boolean(),
  llmClassifierModel: z.string().optional(),
  secondModelReviewEnabled: z.boolean(),
  secondModelReviewModel: z.string().optional(),
  secondModelReviewProvider: z.string().optional(),
  description: z.string().optional(),
});
export type GuardrailProfile = z.infer<typeof GuardrailProfileSchema>;

const GuardrailListSchema = z.object({
  items: z.array(GuardrailProfileSchema),
});

const ComplianceControlSchema = z.object({
  id: z.string(),
  title: z.string(),
  family: z.string().optional(),
  mapping: z.string().optional(),
  detection: z.string().optional(),
  status: z.string().optional(),
  adr: z.string().optional(),
});
export type ComplianceControl = z.infer<typeof ComplianceControlSchema>;

export const COMPLIANCE_FRAMEWORKS = [
  'itar',
  'cmmc_l2',
  'hipaa',
  'gdpr',
  'eu_ai_act',
] as const;
export type ComplianceFramework = (typeof COMPLIANCE_FRAMEWORKS)[number];

const ClassificationScopeSchema = z.object({
  sensitivity: z
    .array(SensitivitySchema)
    .optional(),
  exportControl: z.array(ExportControlSchema).optional(),
});

export const ComplianceProfileSchema = z.object({
  id: z.string().uuid(),
  framework: z.enum(COMPLIANCE_FRAMEWORKS),
  version: z.number(),
  isActive: z.boolean(),
  displayName: z.string(),
  description: z.string().nullable(),
  requiredControls: z.array(ComplianceControlSchema),
  retentionDays: z.number(),
  witnessRequired: z.boolean(),
  classificationScope: ClassificationScopeSchema,
  attestationsRequired: z.array(z.string()),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ComplianceProfile = z.infer<typeof ComplianceProfileSchema>;

const ComplianceListSchema = z.object({
  items: z.array(ComplianceProfileSchema),
});

export const ClassificationRuleSchema = z.object({
  id: z.string().uuid(),
  docTypePattern: z.string(),
  sensitivity: SensitivitySchema,
  exportControl: ExportControlSchema,
  autoApply: z.boolean(),
  priority: z.number(),
  description: z.string().nullable(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  cursor: z.string(),
});
export type ClassificationRule = z.infer<typeof ClassificationRuleSchema>;

const ClassificationListSchema = z.object({
  items: z.array(ClassificationRuleSchema),
  nextCursor: z.string().nullable(),
});

export const SignalWordSchema = z.object({
  word: z.string(),
  ansiClass: z.string(),
  severity: z.enum(['highest', 'high', 'medium', 'low']),
  color: z.string(),
  definition: z.string(),
  atomicWithProcedure: z.boolean(),
  examples: z.array(z.string()).default([]),
});
export type SignalWord = z.infer<typeof SignalWordSchema>;

const SignalWordsResponseSchema = z.object({
  items: z.array(SignalWordSchema),
});

const DeleteResponseSchema = z.object({
  success: z.literal(true),
  deletedRuleId: z.string().uuid(),
});

// ─── Guardrails ─────────────────────────────────────────────────────────────

export const useSysadminGuardrailProfiles = createSysadminQuery({
  key: ['sysadmin-governance-guardrails'],
  url: '/admin/platform/governance/guardrail-profiles',
  schema: GuardrailListSchema,
});

const PatchGuardrailInputSchema = z.object({
  name: z.enum(['basic', 'standard', 'maximum']),
  body: GuardrailProfileSchema.partial().omit({ name: true }),
});

export const usePatchSysadminGuardrailProfile = createSysadminMutation({
  method: 'PATCH',
  url: ({ name }: z.infer<typeof PatchGuardrailInputSchema>) =>
    `/admin/platform/governance/guardrail-profiles/${name}`,
  inputSchema: PatchGuardrailInputSchema,
  outputSchema: GuardrailProfileSchema,
  invalidates: [['sysadmin-governance-guardrails']],
});

// The mutation body the network actually receives is `input.body`. We
// can't unwrap it inside the factory (the factory needs the full input
// to build the URL), so we tell apiClient the body via the url-builder
// pattern of returning a body separately is too clever; instead the
// caller-friendly `.mutateAsync({ name, body: {…} })` shape lets the
// inputSchema validate both halves. apiClient.patch sends the entire
// input object — for PATCH/POST the `body: …` key is on the wire.
//
// Trade-off: the request body has a `name` + `body` envelope shape.
// To keep the wire payload clean, we wrap the mutation in a hook that
// extracts `body` before posting. See useSysadminGuardrailProfilePatch
// below.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import { ExportControlSchema, SensitivitySchema } from '@argusai/shared';
import type { ExportControl, Sensitivity } from '@argusai/shared';

/**
 * Friendlier mutation for PATCH guardrails — accepts
 * `{ name, body }`, sends only `body` over the wire, validates the
 * response against GuardrailProfileSchema.
 *
 * We can't fully express this in the factory because the factory
 * sends the entire input as the body. This thin wrapper is the
 * minimum boilerplate, kept local so other call sites don't
 * accidentally re-roll their own.
 */
export function useSysadminGuardrailPatch() {
  const qc = useQueryClient();
  return useMutation<
    GuardrailProfile,
    Error,
    { name: 'basic' | 'standard' | 'maximum'; body: Partial<Omit<GuardrailProfile, 'name'>> }
  >({
    mutationFn: async ({ name, body }) => {
      const json = await apiClient.patch<unknown>(
        `/admin/platform/governance/guardrail-profiles/${name}`,
        body,
      );
      return GuardrailProfileSchema.parse(json);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-governance-guardrails'] });
    },
  });
}

// ─── Compliance ─────────────────────────────────────────────────────────────

export const useSysadminComplianceProfiles = createSysadminQuery({
  key: ['sysadmin-governance-compliance'],
  url: '/admin/platform/governance/compliance-profiles',
  schema: ComplianceListSchema,
});

export function useSysadminComplianceProfile(framework: ComplianceFramework) {
  return useSysadminQuery({
    key: ['sysadmin-governance-compliance', framework],
    url: `/admin/platform/governance/compliance-profiles/${framework}`,
    schema: ComplianceProfileSchema,
  });
}

export function useSysadminCompliancePatch() {
  const qc = useQueryClient();
  return useMutation<
    ComplianceProfile,
    Error,
    {
      framework: ComplianceFramework;
      body: Partial<{
        displayName: string;
        description: string | null;
        requiredControls: ComplianceControl[];
        retentionDays: number;
        witnessRequired: boolean;
        classificationScope: z.infer<typeof ClassificationScopeSchema>;
        attestationsRequired: string[];
      }>;
    }
  >({
    mutationFn: async ({ framework, body }) => {
      const json = await apiClient.patch<unknown>(
        `/admin/platform/governance/compliance-profiles/${framework}`,
        body,
      );
      return ComplianceProfileSchema.parse(json);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-governance-compliance'] });
      qc.invalidateQueries({
        queryKey: ['sysadmin-governance-compliance', vars.framework],
      });
    },
  });
}

// ─── Classification rules ───────────────────────────────────────────────────

export interface ClassificationRulesFilters {
  cursor?: string;
  limit?: number;
  sensitivity?: Sensitivity;
  exportControl?: ExportControl;
}

export function useSysadminClassificationRules(
  filters: ClassificationRulesFilters = {},
) {
  const params = new URLSearchParams();
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.sensitivity) params.set('sensitivity', filters.sensitivity);
  if (filters.exportControl) params.set('exportControl', filters.exportControl);
  const qs = params.toString();

  return useSysadminQuery({
    key: ['sysadmin-governance-classification-rules', filters],
    url: `/admin/platform/governance/classification-rules${qs ? `?${qs}` : ''}`,
    schema: ClassificationListSchema,
  });
}

const CreateRuleInputSchema = z.object({
  docTypePattern: z.string().min(1).max(200),
  sensitivity: SensitivitySchema,
  exportControl: ExportControlSchema,
  autoApply: z.boolean().default(false),
  priority: z.number().int().min(0).max(1000).default(0),
  description: z.string().max(500).optional(),
});

export const useCreateSysadminClassificationRule = createSysadminMutation({
  method: 'POST',
  url: '/admin/platform/governance/classification-rules',
  inputSchema: CreateRuleInputSchema,
  outputSchema: ClassificationRuleSchema,
  invalidates: [['sysadmin-governance-classification-rules']],
});

export function usePatchSysadminClassificationRule() {
  const qc = useQueryClient();
  return useMutation<
    ClassificationRule,
    Error,
    {
      id: string;
      body: Partial<Omit<ClassificationRule, 'id' | 'createdBy' | 'createdAt' | 'updatedAt' | 'cursor'>>;
    }
  >({
    mutationFn: async ({ id, body }) => {
      const json = await apiClient.patch<unknown>(
        `/admin/platform/governance/classification-rules/${id}`,
        body,
      );
      return ClassificationRuleSchema.parse(json);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['sysadmin-governance-classification-rules'],
      });
    },
  });
}

export const useDeleteSysadminClassificationRule = createSysadminMutation({
  method: 'DELETE',
  url: ({ id }: { id: string }) =>
    `/admin/platform/governance/classification-rules/${id}`,
  outputSchema: DeleteResponseSchema,
  invalidates: [['sysadmin-governance-classification-rules']],
});

// ─── Signal words ───────────────────────────────────────────────────────────

export const useSysadminSignalWords = createSysadminQuery({
  key: ['sysadmin-governance-signal-words'],
  url: '/admin/platform/governance/signal-words',
  schema: SignalWordsResponseSchema,
});

const PatchSignalWordsInputSchema = z.object({
  items: z.array(SignalWordSchema).min(1).max(20),
});

export const usePatchSysadminSignalWords = createSysadminMutation({
  method: 'PATCH',
  url: '/admin/platform/governance/signal-words',
  inputSchema: PatchSignalWordsInputSchema,
  outputSchema: SignalWordsResponseSchema,
  invalidates: [['sysadmin-governance-signal-words']],
});
